import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { AnsweredQuestionWithId } from "@teamprevent/common/model";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";

import { ClinicalNotes } from "../../../clinicalNotes/components/ClinicalNotes";
import { submitGenericCaseClinicalNotes, saveGenericCaseClinicalNotes } from "../actions";
import { AddNextTaskFormModel } from "../../../tasks/models";
import { ApplicationState } from "../../../applicationState";
import { mapAddTaskFormModelToCreateTaskModel } from "../../../task/model";

import { loadTaskTypes } from "../../../tasks/actions";

import { GenericCaseClinicalNotesSections } from "./GenericCaseClinicalNotesSections";

const GenericCaseClinicalNotes: React.FunctionComponent = () => {
    const dispatch = useDispatch();

    const [createTasks, setCreateTasks] = React.useState<AddNextTaskFormModel[]>([]);
    const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);

    const customerIdFromParams = useSelector((state: ApplicationState) => state.router.params?.customerId);
    const genericCaseId = useSelector((state: ApplicationState) => state.router.params!.ohCaseId);
    const taskTypes = useSelector((state: ApplicationState) => state.tasks?.taskTypes);

    React.useEffect(() => {
        if ((!taskTypes || taskTypes.length === 0) && customerIdFromParams && genericCaseId) {
            dispatch(loadTaskTypes(customerIdFromParams, genericCaseId));
        }
    }, [customerIdFromParams, dispatch, genericCaseId, taskTypes]);

    const setTasks = React.useCallback((tasks: AddNextTaskFormModel[]) => {
        setCreateTasks(tasks);
    }, []);

    const save = React.useCallback((ohCaseId: string, taskId: string, customerId: string, answeredQuestions: AnsweredQuestionWithId[], eTag: string | null) =>
        dispatch(saveGenericCaseClinicalNotes(customerId, ohCaseId, taskId, answeredQuestions, eTag)), [dispatch]);

    const submit = React.useCallback(async (ohCaseId: string, taskId: string, customerId: string) => {
        setIsSubmitted(true);

        if (createTasks?.some(ct => !ct.isValid)) {
            return;
        }

        const nextTasks = createTasks.filter(t => !!t.task).map(t => mapAddTaskFormModelToCreateTaskModel(t.task!, ohCaseId));

        await dispatch(submitGenericCaseClinicalNotes(customerId, ohCaseId, taskId, nextTasks));
        dispatch(navigateToParentRoute());
    }, [createTasks, dispatch]);

    return (
        <ClinicalNotes saveQuestionnaire={save} submitQuestionnaire={submit}>
            <GenericCaseClinicalNotesSections
                ohCaseId={genericCaseId}
                taskTypes={taskTypes}
                setTasks={setTasks}
                isSubmitted={isSubmitted}
            />
        </ClinicalNotes>
    );
};

export { GenericCaseClinicalNotes };
